/**
 * Auth API Client for Cognito Authentication
 *
 * This client handles all authentication operations with AWS Cognito
 * using the Cognito hosted UI and token management.
 *
 * DEV_MODE: When VITE_DEV_MODE=true, bypasses Cognito and uses mock user.
 * This is for local development with LocalStack (no Cognito available).
 */

const COGNITO_REGION = import.meta.env.VITE_COGNITO_REGION || 'us-east-1';
const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';

// DEV_MODE: Only enabled during local development AND when running on localhost
// This double-check prevents dev mode from EVER being enabled in staging/production,
// even if VITE_DEV_MODE accidentally leaks into a build.
const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// Log a warning if DEV_MODE env var is set but we're not on localhost
if (import.meta.env.VITE_DEV_MODE === 'true' && !isLocalhost) {
  console.error(
    '[Auth] SECURITY: VITE_DEV_MODE is set but hostname is not localhost. ' +
      'DEV_MODE has been disabled. This should never happen in production builds.',
  );
}

const DEV_MODE = import.meta.env.VITE_DEV_MODE === 'true' && isLocalhost;

// Dev users for local development (dev@localhost through dev10@localhost)
// These users can NEVER authenticate in staging/production because:
// 1. DEV_MODE requires VITE_DEV_MODE=true AND hostname=localhost
// 2. Backend dev mode requires missing Cognito config (always present in staging/prod)
// 3. VITE_DEV_MODE is tree-shaken from production builds
const DEV_USERS: AuthUser[] = Array.from({ length: 10 }, (_, i) => ({
  userId: `dev-user-${String(i + 1).padStart(3, '0')}`,
  username: `devuser${i + 1}`,
  email: i === 0 ? 'dev@localhost' : `dev${i + 1}@localhost`,
  roles: ['user', 'admin', 'contributor', 'beta_tester'],
  emailVerified: true,
}));

function getDevTokens(userIndex: number): AuthTokens {
  const suffix = userIndex === 0 ? '' : `-${userIndex + 1}`;
  return {
    accessToken: `dev-token${suffix}`,
    idToken: `dev-token${suffix}`,
    refreshToken: `dev-refresh-token${suffix}`,
    expiresAt: Date.now() + 365 * 24 * 60 * 60 * 1000, // 1 year from now
  };
}

const DEV_USER_STORAGE_KEY = 'fias_dev_user_index';

function getSelectedDevUserIndex(): number {
  if (!DEV_MODE) return 0;
  try {
    const stored = localStorage.getItem(DEV_USER_STORAGE_KEY);
    if (stored !== null) {
      const index = parseInt(stored, 10);
      if (index >= 0 && index < DEV_USERS.length) return index;
    }
  } catch {
    // Ignore storage errors
  }
  return 0;
}

function setSelectedDevUserIndex(index: number): void {
  localStorage.setItem(DEV_USER_STORAGE_KEY, String(index));
}

const AUTH_STORAGE_KEY = 'fias_auth';

// Version number for storage format - increment when changing StoredAuth structure
const AUTH_STORAGE_VERSION = 1;

export interface AuthUser {
  userId: string;
  username: string;
  email: string;
  roles: string[];
  emailVerified: boolean;
}

interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

interface StoredAuth {
  version: number;
  clientId: string; // Track which Cognito client ID the tokens are for
  tokens: AuthTokens;
  user: AuthUser;
}

interface CognitoError {
  __type: string;
  message: string;
}

class AuthApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com`;
  }

  private getStoredAuth(): StoredAuth | null {
    try {
      const stored = localStorage.getItem(AUTH_STORAGE_KEY);
      if (!stored) return null;

      const parsed = JSON.parse(stored) as StoredAuth;

      // Clear stale auth data from old versions or different environments
      if (!parsed.version || parsed.version !== AUTH_STORAGE_VERSION) {
        console.warn('[Auth] Clearing stale auth data: version mismatch');
        this.clearStoredAuth();
        return null;
      }

      // Clear auth data if it's from a different Cognito client (environment change)
      if (parsed.clientId && parsed.clientId !== COGNITO_CLIENT_ID) {
        console.warn('[Auth] Clearing stale auth data: client ID mismatch (environment change)');
        this.clearStoredAuth();
        return null;
      }

      return parsed;
    } catch {
      // If parsing fails, clear the corrupt data
      this.clearStoredAuth();
      return null;
    }
  }

  private setStoredAuth(auth: StoredAuth): void {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
  }

  private clearStoredAuth(): void {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }

  private decodeJwt(token: string): Record<string, unknown> {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  }

  private extractUserFromTokens(idToken: string): AuthUser {
    const payload = this.decodeJwt(idToken);
    return {
      // internal_user_id is set by PreTokenGeneration Lambda (without 'custom:' prefix)
      userId:
        (payload['internal_user_id'] as string) ||
        (payload['custom:internal_user_id'] as string) ||
        (payload['sub'] as string),
      // username is set by PreTokenGeneration Lambda
      username: (payload['username'] as string) || '',
      email: payload['email'] as string,
      // user_roles is set by PreTokenGeneration Lambda (without 'custom:' prefix)
      roles: (
        (payload['user_roles'] as string) ||
        (payload['custom:roles'] as string) ||
        'user'
      ).split(','),
      emailVerified: payload['email_verified'] as boolean,
    };
  }

  private async cognitoRequest<T>(action: string, body: Record<string, unknown>): Promise<T> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`,
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();

    if (!response.ok) {
      const error = data as CognitoError;
      const errorType = error.__type?.split('#').pop() || 'UnknownError';
      throw new Error(this.mapCognitoError(errorType, error.message));
    }

    return data as T;
  }

  private mapCognitoError(type: string, message: string): string {
    switch (type) {
      case 'NotAuthorizedException':
        return 'Incorrect email or password';
      case 'UserNotFoundException':
        return 'No account found with this email';
      case 'UsernameExistsException':
        return 'An account with this email already exists';
      case 'InvalidPasswordException':
        return 'Password does not meet requirements. Must be at least 8 characters with uppercase, lowercase, number, and special character.';
      case 'CodeMismatchException':
        return 'Invalid verification code';
      case 'ExpiredCodeException':
        return 'Verification code has expired. Please request a new one.';
      case 'UserNotConfirmedException':
        return 'Please verify your email before signing in';
      case 'LimitExceededException':
        return 'Too many attempts. Please try again later.';
      case 'InvalidParameterException':
        if (message.includes('inviteCode')) {
          return 'Invalid invite code. Please request access from the Fias team.';
        }
        return message;
      default:
        return message || 'An error occurred. Please try again.';
    }
  }

  async signUp(
    email: string,
    password: string,
    inviteCode: string,
    username: string,
  ): Promise<{ requiresConfirmation: boolean }> {
    // Normalize email to lowercase (emails are case-insensitive per RFC 5321)
    const normalizedEmail = email.toLowerCase();

    interface SignUpResponse {
      UserConfirmed: boolean;
      UserSub: string;
    }

    const response = await this.cognitoRequest<SignUpResponse>('SignUp', {
      ClientId: COGNITO_CLIENT_ID,
      Username: normalizedEmail,
      Password: password,
      UserAttributes: [{ Name: 'email', Value: normalizedEmail }],
      ClientMetadata: {
        inviteCode,
        username,
      },
    });

    return {
      requiresConfirmation: !response.UserConfirmed,
    };
  }

  async confirmSignUp(
    email: string,
    code: string,
    inviteCode?: string,
    username?: string,
  ): Promise<void> {
    // Normalize email to lowercase (emails are case-insensitive per RFC 5321)
    const normalizedEmail = email.toLowerCase();

    const clientMetadata: Record<string, string> = {};
    if (inviteCode) clientMetadata.inviteCode = inviteCode;
    if (username) clientMetadata.username = username;

    await this.cognitoRequest('ConfirmSignUp', {
      ClientId: COGNITO_CLIENT_ID,
      Username: normalizedEmail,
      ConfirmationCode: code,
      ...(Object.keys(clientMetadata).length > 0 && { ClientMetadata: clientMetadata }),
    });
  }

  async signIn(email: string, password: string): Promise<{ user: AuthUser }> {
    // Normalize email to lowercase (emails are case-insensitive per RFC 5321)
    const normalizedEmail = email.toLowerCase();

    // DEV_MODE: Skip Cognito, return mock user immediately
    if (DEV_MODE) {
      const devIndex = getSelectedDevUserIndex();
      const devUser = DEV_USERS[devIndex];
      const devTokens = getDevTokens(devIndex);
      console.warn(`[DEV_MODE] Bypassing Cognito authentication - using ${devUser.email}`);
      this.setStoredAuth({
        version: AUTH_STORAGE_VERSION,
        clientId: COGNITO_CLIENT_ID,
        tokens: devTokens,
        user: devUser,
      });
      return { user: devUser };
    }

    interface AuthResult {
      AuthenticationResult: {
        AccessToken: string;
        IdToken: string;
        RefreshToken: string;
        ExpiresIn: number;
      };
    }

    const response = await this.cognitoRequest<AuthResult>('InitiateAuth', {
      ClientId: COGNITO_CLIENT_ID,
      AuthFlow: 'USER_PASSWORD_AUTH',
      AuthParameters: {
        USERNAME: normalizedEmail,
        PASSWORD: password,
      },
    });

    const { AuthenticationResult } = response;
    const user = this.extractUserFromTokens(AuthenticationResult.IdToken);
    const tokens: AuthTokens = {
      accessToken: AuthenticationResult.AccessToken,
      idToken: AuthenticationResult.IdToken,
      refreshToken: AuthenticationResult.RefreshToken,
      expiresAt: Date.now() + AuthenticationResult.ExpiresIn * 1000,
    };

    this.setStoredAuth({
      version: AUTH_STORAGE_VERSION,
      clientId: COGNITO_CLIENT_ID,
      tokens,
      user,
    });

    return { user };
  }

  async signOut(): Promise<void> {
    // DEV_MODE: Just clear local storage, skip Cognito
    if (DEV_MODE) {
      console.warn('[DEV_MODE] Signing out mock user');
      this.clearStoredAuth();
      return;
    }

    const stored = this.getStoredAuth();
    if (stored?.tokens.accessToken) {
      try {
        await this.cognitoRequest('GlobalSignOut', {
          AccessToken: stored.tokens.accessToken,
        });
      } catch {
        // Ignore errors during sign out
      }
    }
    this.clearStoredAuth();
  }

  async getCurrentUser(): Promise<AuthUser | null> {
    // DEV_MODE: Auto-login with selected dev user if not already logged in
    if (DEV_MODE) {
      const stored = this.getStoredAuth();
      if (!stored) {
        const devIndex = getSelectedDevUserIndex();
        const devUser = DEV_USERS[devIndex];
        const devTokens = getDevTokens(devIndex);
        console.warn(`[DEV_MODE] Auto-logging in as ${devUser.email}`);
        this.setStoredAuth({
          version: AUTH_STORAGE_VERSION,
          clientId: COGNITO_CLIENT_ID,
          tokens: devTokens,
          user: devUser,
        });
        return devUser;
      }
      return stored.user;
    }

    const stored = this.getStoredAuth();
    if (!stored) return null;

    // Check if token is expired
    if (Date.now() >= stored.tokens.expiresAt - 60000) {
      // Refresh token if expired (with 1 minute buffer)
      try {
        await this.refreshTokens();
        const refreshed = this.getStoredAuth();
        return refreshed?.user || null;
      } catch {
        this.clearStoredAuth();
        return null;
      }
    }

    return stored.user;
  }

  async refreshTokens(): Promise<void> {
    const stored = this.getStoredAuth();
    if (!stored?.tokens.refreshToken) {
      throw new Error('No refresh token available');
    }

    interface RefreshResult {
      AuthenticationResult: {
        AccessToken: string;
        IdToken: string;
        ExpiresIn: number;
      };
    }

    const response = await this.cognitoRequest<RefreshResult>('InitiateAuth', {
      ClientId: COGNITO_CLIENT_ID,
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      AuthParameters: {
        REFRESH_TOKEN: stored.tokens.refreshToken,
      },
    });

    const { AuthenticationResult } = response;
    const user = this.extractUserFromTokens(AuthenticationResult.IdToken);
    const tokens: AuthTokens = {
      accessToken: AuthenticationResult.AccessToken,
      idToken: AuthenticationResult.IdToken,
      refreshToken: stored.tokens.refreshToken, // Keep existing refresh token
      expiresAt: Date.now() + AuthenticationResult.ExpiresIn * 1000,
    };

    this.setStoredAuth({
      version: AUTH_STORAGE_VERSION,
      clientId: COGNITO_CLIENT_ID,
      tokens,
      user,
    });
  }

  async forgotPassword(email: string): Promise<void> {
    // Normalize email to lowercase (emails are case-insensitive per RFC 5321)
    const normalizedEmail = email.toLowerCase();

    await this.cognitoRequest('ForgotPassword', {
      ClientId: COGNITO_CLIENT_ID,
      Username: normalizedEmail,
    });
  }

  async confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    // Normalize email to lowercase (emails are case-insensitive per RFC 5321)
    const normalizedEmail = email.toLowerCase();

    await this.cognitoRequest('ConfirmForgotPassword', {
      ClientId: COGNITO_CLIENT_ID,
      Username: normalizedEmail,
      ConfirmationCode: code,
      Password: newPassword,
    });
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    const stored = this.getStoredAuth();
    if (!stored?.tokens.accessToken) {
      throw new Error('Not authenticated');
    }

    await this.cognitoRequest('ChangePassword', {
      AccessToken: stored.tokens.accessToken,
      PreviousPassword: oldPassword,
      ProposedPassword: newPassword,
    });
  }

  async resendConfirmationCode(email: string): Promise<void> {
    // Normalize email to lowercase (emails are case-insensitive per RFC 5321)
    const normalizedEmail = email.toLowerCase();

    await this.cognitoRequest('ResendConfirmationCode', {
      ClientId: COGNITO_CLIENT_ID,
      Username: normalizedEmail,
    });
  }

  getAccessToken(): string | null {
    const stored = this.getStoredAuth();
    return stored?.tokens.accessToken || null;
  }

  getIdToken(): string | null {
    const stored = this.getStoredAuth();
    return stored?.tokens.idToken || null;
  }

  /**
   * Returns whether the client is running in DEV_MODE.
   * Only true when VITE_DEV_MODE=true AND hostname is localhost.
   */
  isDevMode(): boolean {
    return DEV_MODE;
  }

  /**
   * Returns the list of available dev users.
   * Only useful in DEV_MODE - returns empty array otherwise.
   */
  getDevUsers(): AuthUser[] {
    if (!DEV_MODE) return [];
    return [...DEV_USERS];
  }

  /**
   * Switches to a different dev user by index (0-9).
   * Only works in DEV_MODE. Clears current auth and logs in as the selected user.
   * Returns the new user, or null if not in DEV_MODE.
   */
  switchDevUser(index: number): AuthUser | null {
    if (!DEV_MODE) return null;
    if (index < 0 || index >= DEV_USERS.length) return null;

    setSelectedDevUserIndex(index);
    const devUser = DEV_USERS[index];
    const devTokens = getDevTokens(index);

    console.warn(`[DEV_MODE] Switching to ${devUser.email} (${devUser.userId})`);
    this.setStoredAuth({
      version: AUTH_STORAGE_VERSION,
      clientId: COGNITO_CLIENT_ID,
      tokens: devTokens,
      user: devUser,
    });

    return devUser;
  }

  /**
   * Gets a valid access token, refreshing if expired or about to expire.
   * Use this instead of getAccessToken() when making API calls.
   */
  async getValidAccessToken(): Promise<string | null> {
    // DEV_MODE: Return mock token immediately
    if (DEV_MODE) {
      return getDevTokens(getSelectedDevUserIndex()).accessToken;
    }

    const stored = this.getStoredAuth();
    if (!stored) return null;

    // Check if token is expired or about to expire (within 1 minute)
    if (Date.now() >= stored.tokens.expiresAt - 60000) {
      try {
        await this.refreshTokens();
        const refreshed = this.getStoredAuth();
        return refreshed?.tokens.accessToken || null;
      } catch {
        this.clearStoredAuth();
        return null;
      }
    }

    return stored.tokens.accessToken;
  }

  /**
   * Gets a valid ID token, refreshing if expired or about to expire.
   * Use this instead of getIdToken() when making API calls or WebSocket connections.
   */
  async getValidIdToken(): Promise<string | null> {
    // DEV_MODE: Return mock token immediately
    if (DEV_MODE) {
      return getDevTokens(getSelectedDevUserIndex()).idToken;
    }

    const stored = this.getStoredAuth();
    if (!stored) return null;

    // Check if token is expired or about to expire (within 1 minute)
    if (Date.now() >= stored.tokens.expiresAt - 60000) {
      try {
        await this.refreshTokens();
        const refreshed = this.getStoredAuth();
        return refreshed?.tokens.idToken || null;
      } catch {
        this.clearStoredAuth();
        return null;
      }
    }

    return stored.tokens.idToken;
  }
}

export const authApi = new AuthApiClient();
export default authApi;
